'use client'

import { RefObject, useEffect, useId, useRef } from 'react'

import {
  isTopEscapeOwner,
  pushEscapeOwner,
  removeEscapeOwner,
} from '../../hooks/escapeStack'

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const NESTED_OVERLAY_SELECTOR =
  '[role="dialog"], [aria-modal="true"], [data-overlay-root]'

const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))

const controlsOverlay = (element: HTMLElement, overlayId: string): boolean =>
  (element.getAttribute('aria-controls') ?? '').split(/\s+/).includes(overlayId)

const isOwnedNestedOverlay = (
  overlay: HTMLElement,
  wrapper: HTMLElement
): boolean =>
  Boolean(
    overlay.id &&
      Array.from(wrapper.querySelectorAll<HTMLElement>('[aria-controls]')).some(
        (element) => controlsOverlay(element, overlay.id)
      )
  )

// Module-level stack to track open overlay IDs. Only the top-most overlay
// (Modal or Drawer) should respond to Escape/Tab so nested overlays don't
// interfere - a Modal opened above a Drawer owns the keyboard while the
// Drawer's trap idles underneath.
const _overlayStack: string[] = []

export type UseOverlayStackProps = {
  isOpen: boolean
  onClose: () => void
  wrapperRef: RefObject<HTMLDivElement>
  /**
   * Set false to keep Escape from closing the overlay — for a form whose
   * in-progress input would be lost. The overlay still owns the key while it
   * is top of the stack, so it never falls through to an ancestor dialog.
   */
  closeOnEsc?: boolean
}

/**
 * Shared overlay machinery for Modal and Drawer: stack membership (top-most
 * overlay owns the keyboard), Escape-to-close, a Tab focus trap scoped to
 * `wrapperRef`, and focus save/restore across open/close.
 */
export const useOverlayStack = ({
  isOpen,
  onClose,
  wrapperRef,
  closeOnEsc = true,
}: UseOverlayStackProps): void => {
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Stable SSR-safe ID for this overlay instance
  const overlayId = useId()

  // Push/pop this overlay's ID on the stack while it is open
  useEffect(() => {
    if (!isOpen) return
    _overlayStack.push(overlayId)
    return () => {
      const idx = _overlayStack.lastIndexOf(overlayId)
      if (idx !== -1) _overlayStack.splice(idx, 1)
    }
  }, [isOpen, overlayId])

  // Save/restore focus across open/close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement
    } else {
      previousFocusRef.current?.focus()
    }
  }, [isOpen])

  // Focus the wrapper div when the overlay opens so no button gets an
  // immediate focus ring. setTimeout(0) defers past any pending keyboard
  // event (e.g. an Enter keyup from the field that triggered the overlay).
  // Keyboard users can Tab from here to reach the buttons.
  useEffect(() => {
    if (!isOpen) return
    const wrapper = wrapperRef.current
    if (!wrapper) return
    const timer = setTimeout(() => {
      wrapper.focus()
    }, 0)
    return () => clearTimeout(timer)
  }, [isOpen, wrapperRef])

  // Escape ownership lives in the shared stack, so a dropdown open inside this
  // overlay dismisses alone on the first press.
  useEffect(() => {
    if (!isOpen) return
    pushEscapeOwner(overlayId)
    return () => removeEscapeOwner(overlayId)
  }, [isOpen, overlayId])

  // Escape, in the CAPTURE phase. A Chakra v1 dialog around this overlay (every
  // modal spawned from the incident drawer) handles Escape on a React
  // `onKeyDown` that calls stopPropagation() before closing, and it is a
  // React-tree ancestor even though this overlay portals to body. A
  // bubble-phase document listener therefore never runs: the drawer would close
  // and this overlay would never see the key. (ENG-32100)
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (!isTopEscapeOwner(overlayId)) return

      // Same rule as the Tab trap below: a nested or foreign overlay holding
      // focus owns the keyboard, so don't close underneath it.
      const active = document.activeElement
      if (
        active &&
        !wrapperRef.current?.contains(active) &&
        active.closest(NESTED_OVERLAY_SELECTOR)
      ) {
        return
      }

      e.stopPropagation()
      if (closeOnEsc) onClose()
    }

    document.addEventListener('keydown', handleEscape, true)
    return () => document.removeEventListener('keydown', handleEscape, true)
  }, [isOpen, onClose, overlayId, wrapperRef, closeOnEsc])

  // Tab trap - only the top-most overlay in the stack handles this
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore events if a newer overlay is on top of this one
      if (_overlayStack[_overlayStack.length - 1] !== overlayId) return

      // A foreign non-stack overlay (e.g. a Chakra drawer) owns the keyboard
      // while it holds focus. A portalled overlay controlled from inside this
      // wrapper is instead part of this modal's focus sequence, so Tab can
      // return to the wrapper when it reaches the portalled boundary. Focus
      // merely sitting elsewhere (a toast action, a programmatic .focus()) is
      // pulled back by the standard trap below.
      const active = document.activeElement
      const nestedOverlay =
        active &&
        !wrapperRef.current?.contains(active) &&
        active.closest<HTMLElement>(NESTED_OVERLAY_SELECTOR)

      if (nestedOverlay) {
        if (e.key !== 'Tab') return

        const wrapper = wrapperRef.current
        if (!wrapper || !isOwnedNestedOverlay(nestedOverlay, wrapper)) return

        const nestedFocusable = getFocusableElements(nestedOverlay)
        const firstNested = nestedFocusable[0]
        const lastNested = nestedFocusable[nestedFocusable.length - 1]
        const isLeavingNestedOverlay = e.shiftKey
          ? active === firstNested
          : active === lastNested

        if (!isLeavingNestedOverlay) return

        const wrapperFocusable = getFocusableElements(wrapper)
        const ownerIndexes = wrapperFocusable.reduce<number[]>(
          (indexes, element, index) => {
            if (controlsOverlay(element, nestedOverlay.id)) indexes.push(index)
            return indexes
          },
          []
        )
        const firstOwnerIndex = ownerIndexes[0] ?? 0
        const lastOwnerIndex = ownerIndexes[ownerIndexes.length - 1] ?? -1
        const target = e.shiftKey
          ? wrapperFocusable[firstOwnerIndex - 1] ??
            wrapperFocusable[wrapperFocusable.length - 1]
          : wrapperFocusable[lastOwnerIndex + 1] ?? wrapperFocusable[0]

        e.preventDefault()
        ;(target ?? wrapper).focus()
        return
      }

      if (e.key !== 'Tab') return
      const wrapper = wrapperRef.current
      if (!wrapper) return
      const focusable = getFocusableElements(wrapper)
      // If focus escaped the overlay entirely, pull it back in
      if (!wrapper.contains(document.activeElement)) {
        e.preventDefault()
        if (focusable.length > 0) {
          focusable[0].focus()
        } else {
          wrapper.focus()
        }
        return
      }
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, overlayId, wrapperRef])
}
